import {getElementByElementOrId} from "../../../helpers/element";
import {MixinStateMachine} from "../../../lib/mixins/stateable";
import stateMachine from "../instances/state_machine";
import {ClosePaneComponent, ClosePaneComponentConfig} from "../components/close_pane_component";
import {PaneOptionsComponent, PaneOptionsComponentConfig} from "../components/pane_options_component";

export const PaneGroupConfig = {
    primaryGroupCode: 'PRIMARY',
    secondaryGroupCode: 'SECONDARY'
};

/**
 * Class representing a pane of content in the nav.
 * */
export class Pane {

    /**
     * Create and return a new Pane instance from the passed in HTML element.
     * @param {HTMLElement} element - The DOM element for the Pane to be created.
     * @return {Pane} A Pane object.
     */
    static new(element) {
        if (!element) {
            return null
        }
        return new Pane(element)
    }

    /**
     * Create a Pane instance.
     *
     * Attached / Detached state decides if the element is rendered in the view. Detaching sets `display: none;` and therefore improves rendering performance.
     *
     * @param {String|HTMLElement} id_or_element - The id string for an element or a DOM element reference.
     */
    constructor(id_or_element) {
        this.element = getElementByElementOrId(id_or_element);
        this.identifier = getIdentifier(this.element);
        this.statsIdentifier = getStatsIdentifier(this.element);

        /**
         * Whether this pane is attached or not.
         * This "attachment" relates to whether the pane should be included in the layers that are rendered by the browser's rendering engine.
         * Essentially when not attached the element should be `display: none;` (or the element could be removed from the DOM) - this greatly improves rendering performance.
         * We can use the before focus and after blur callbacks to set the attachment state (and render any changes).
         *
         * @name Pane#isAttached
         * @type Boolean
         * @default false
         */
        this.isAttached = getIsAttached(this.element);

        this.group = null;

        /**
         * An array of callback functions that will be triggered before the pane is blurred.
         * @name Pane#onBeforeBlurCallbacks
         * @type {Array.Function}
         * @default []
         */
        this.onBeforeBlurCallbacks = [];
        this.onAfterBlurCallbacks = [];
        this.onBeforeFocusCallbacks = [];
        this.onAfterFocusCallbacks = [];

        // All panes have an after focus callback to track being opened. It counts only a real change of
        // pane that the nav didn't make itself: re-focusing the pane that's already current opened
        // nothing (plans/2026-09-24--nav-programmatic-selections-counted.md).
        this.onAfterFocusCallbacks.push((focus = {}) => {
            if (focus.paneChanged === false) return;
            if (this.getNav && this.getNav()?.isUntrackedSelection?.()) return;
            if (this.trackOpened) this.trackOpened()
        });

        this.closePaneComponents = constructClosePaneComponents(this.element, this);

        MixinStateMachine(this); // Add the state machine management methods to `this`.
        this.setStateMachine(stateMachine); // Set `this.stateMachine`.

        this.paneOptionsComponents = getPaneOptionsComponents(this);
    }

    /**
     * Returns whether the Pane (via the global `openedPaneIds` state and `this.paneIdentifier`) has ever been opened before.
     *
     * @return {boolean} The opened state.
     */
    hasOpened() {
        let openedPaneIds = this.readStateMachineValue('openedPaneIds');
        if (!openedPaneIds) return false;
        return openedPaneIds.includes(this.identifier);
    }

    // A pane is focused when it is the pane the user is currently able to interact with.
    isFocused() {
        return this.readStateMachineValue('currentPaneId') === this.identifier;
    }

    // A pane was previously focused when it is the pane the user was last able to interact with.
    wasPreviouslyFocused() {
        return this.readStateMachineValue('prevPaneId') === this.identifier;
    }

    // A pane is active when it is within the current path hierarchy (ie. the primary (root) pane is always active).
    isActive() {
        if (this.isPrimaryPane()) {
            return true
        }
        return !!this.isFocused();
    }

    isPrimaryPane() {
        return this.group === PaneGroupConfig.primaryGroupCode;
    }

    isSecondaryPane() {
        return this.group === PaneGroupConfig.secondaryGroupCode;
    }

    readInternalStateValue(stateKey) {
        return this.internalState()[stateKey];
    }

    internalState() {
        return {
            opened: this.hasOpened(),
            focused: this.isFocused(),
            active: this.isActive(),
        }
    }

    onBeforeBlur() {
        this.onBeforeBlurCallbacks.forEach((callback) => {
            callback();
        });
        // alert(`Pane ${ this.identifier } is about to BLUR`);
    }

    onAfterBlur() {
        this.onAfterBlurCallbacks.forEach((callback) => {
            callback();
        });
        // alert(`Pane ${ this.identifier } has become BLURRED`);
    }

    onBeforeFocus() {
        this.onBeforeFocusCallbacks.forEach((callback) => {
            callback();
        });
        // alert(`Pane ${ this.identifier } is about to FOCUS`);
    }

    // `focus.paneChanged` is false when the pane was already the current one. The callbacks still run
    // (starting impression observers relies on it at boot); only the open tracking skips.
    onAfterFocus(focus = {}) {
        this.onAfterFocusCallbacks.forEach((callback) => {
            callback(focus);
        });
        // alert(`Pane ${ this.identifier } has become FOCUSED`);
    }

    /**
     * Detach the Pane instance from the browser rendering layers.
     * The paneComponent renders the `isAttached` state.
     * We do the render after a delay so that any animations can complete before we detach (no longer display) the pane.
     */
    detach() {
        this.isAttached = false;
        if (!this.paneComponent) return;
        setTimeout(() => {

            requestAnimationFrame((timestamp) => {
                this.paneComponent.renderAttachedState(this.isAttached);
            });

        }, 1000); // Some delay so that any animations can complete before we no longer display the pane.
    }

    /**
     * Attach the Pane instance to the browser rendering layers.
     * The paneComponent renders the `isAttached` state.
     */
    attach() {
        this.isAttached = true;
        if (!this.paneComponent) return;
        this.paneComponent.renderAttachedState(this.isAttached);
    }

}

function getIdentifier(element) {
    return element.dataset['nav_pane__identifier'];
}

function getStatsIdentifier(element) {
    return element.dataset['nav_pane__identifier'];
}

function getIsAttached(element) {
    return element.dataset['nav_pane__is_attached'] === 'true';
}

function constructClosePaneComponents(parentElement, pane) {
    let components = [];
    let elements = parentElement.querySelectorAll(ClosePaneComponentConfig.selector);
    elements.forEach((element) => {
        let component = ClosePaneComponent.new(element, pane);
        components.push(component);
    });
    return components;
}

function getPaneOptionsComponents(pane) {
    let paneOptionsComponents = [];
    const paneOptionsElements = pane.element.querySelectorAll(PaneOptionsComponentConfig.selector);
    paneOptionsElements.forEach((element) => {
        let component = new PaneOptionsComponent(element, pane);
        paneOptionsComponents.push(component);
    });
    return paneOptionsComponents;
}

